import React, { useCallback } from 'react';
import EDButtonSelect from '../../../../../../BaseComponents/Buttons/ButtonSelect/EDButtonSelect';
import EDIcon from '../../../../../../BaseComponents/Icon/EDIcon';
import EDTab from '../../../../../../BaseComponents/Tabs/Tab/EDTab';
import EDTabs from '../../../../../../BaseComponents/Tabs/EDTabs';
import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import EDTooltip from '../../../../../../BaseComponents/Tooltip/EDTooltip';
import { useIsSmallScreen } from '../../../../../../Hooks/useFullscreenView';
import { ISidTyp } from '../ObjektVy/ObjektVy';

interface IVyValjarProps {
  setAktuellaVyer: (value: number[]) => void;
  readonly vyer: ISidTyp[];
  setVyMenuNode?: React.Dispatch<React.SetStateAction<HTMLDivElement | null>>;
  tabbed?: boolean;
  /** Sida vid sida kan väljas */
  sidaVidSida?: boolean;
  fullScreen?: boolean;
  showDetailsRight?: boolean;
}

const useStyles = makeStyles<IVyValjarProps>()(
  (theme: Theme, props: IVyValjarProps) =>
    ({
      tabs: {
        flex: 1,
      },
      flexContainer: {
        flex: 1,
      } as const,
      tab: {
        color: theme.palette.common.white,
      } as const,
      flexTab: {} as const,
      headerContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: theme.customProperties.VyValjareColors.tabs,
        color: `${theme.palette.common.white} !important`,
        boxShadow: theme.shadows[3],
        flex: 'none',
        height: 40,
        maxHeight: 48,
        zIndex: 1000,
        width: '100%',
      } as const,
      sideBySideButton: {
        minHeight: 0,
      } as const,
      noHeaderContainer: {
        position: 'relative',
      },
      floatingMenu: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        borderRadius: '50%',
      },
    } as const)
);

const ButtonVyValjare = (props: IVyValjarProps) => {
  const { setAktuellaVyer } = props;
  const setAktuellVy = React.useCallback<
    (id?: string | number | undefined) => void
  >(
    aktuellVy => {
      if (typeof aktuellVy === 'number') {
        return setAktuellaVyer([aktuellVy]);
      }
    },
    [setAktuellaVyer]
  );
  const aktuellaVyer = props.vyer.reduce<number[]>(
    (acc, curr, idx) => (curr.aktiv ? [...acc, idx] : acc),
    []
  );
  return (
    <EDButtonSelect
      label={<EDIcon iconType="more_horiz" />}
      value={aktuellaVyer[0]}
      selectItem={setAktuellVy}
      items={props.vyer.map((vy, idx) => ({
        id: idx.toString(),
        label: vy.caption || 'caption saknas',
      }))}
    />
  );
};

const TabbedVyValjare = (props: IVyValjarProps) => {
  const { setAktuellaVyer } = props;
  const { classes, cx } = useStyles(props, {
    props: props as any,
  });
  const [sideBySideMode, setSideBySideMode] = React.useState(false);
  const [lastVisitedTab, setLastVisitedTab] = React.useState(0);
  const aktuellaVyer = props.vyer.reduce<number[]>(
    (acc, curr, idx) => (curr.aktiv ? [...acc, idx] : acc),
    []
  );
  const isSmall = useIsSmallScreen();
  const setAktuellVy = useCallback<(id: number) => void>(
    aktuellTab => {
      setSideBySideMode(oldVal => (aktuellTab === 0 ? !oldVal : false));
      if (aktuellTab !== 0) {
        setLastVisitedTab(aktuellTab - 1);
      }
      setAktuellaVyer(
        props.sidaVidSida
          ? aktuellTab === 0
            ? sideBySideMode
              ? [lastVisitedTab]
              : [0, 1]
            : [aktuellTab - 1]
          : [aktuellTab]
      );
    },
    [setAktuellaVyer, props.sidaVidSida, sideBySideMode, lastVisitedTab]
  );

  const vyMenuRef = (node: HTMLDivElement) => {
    props.setVyMenuNode?.(node);
  };

  return props.vyer.length > 1 ? (
    <div className={classes.headerContainer}>
      <EDTabs
        value={
          aktuellaVyer.length !== 1
            ? 0
            : aktuellaVyer[0] + (props.sidaVidSida ? 1 : 0)
        }
        onChange={setAktuellVy}
        classes={{ flexContainer: classes.flexContainer, root: classes.tabs }}
        indicatorColor="white"
        variant="scrollable"
        noBoxShadow
      >
        {props.sidaVidSida && (
          <EDTab
            key="sidor"
            value={0}
            icon={
              <EDTooltip
                title={
                  aktuellaVyer.length > 1
                    ? 'Stäng sida-vid-sida läget'
                    : `Visa ${props.vyer[0].caption} sida vid sida med ${props.vyer[1].caption}`
                }
              >
                <span>
                  <EDIcon iconType="vertical_split_screen" color="inherit" />
                </span>
              </EDTooltip>
            }
            className={cx(classes.tab, classes.sideBySideButton)}
            small
          />
        )}
        {props.vyer
          .map((vy, idx) => ({ ...vy, originalIdx: idx }))
          .filter(vy => !vy.hidden)
          .map(vy => {
            return (
              <EDTab
                key={vy.originalIdx}
                value={vy.originalIdx + (props.sidaVidSida ? 1 : 0)}
                label={
                  <EDTypography variant="overline" color="inherit">
                    {vy.caption}
                  </EDTypography>
                }
                className={classes.tab}
                spaciousPadding={!isSmall}
                hidden={vy.hidden}
              />
            );
          })}
      </EDTabs>

      <div ref={vyMenuRef} />
    </div>
  ) : (
    <div className={classes.noHeaderContainer}>
      <div ref={vyMenuRef} className={classes.floatingMenu} />
    </div>
  );
};

const VyValjare = (props: IVyValjarProps) => {
  return (
    <React.Fragment>
      {props.tabbed ? (
        <TabbedVyValjare {...props} />
      ) : (
        <ButtonVyValjare {...props} />
      )}
    </React.Fragment>
  );
};

export default VyValjare;
